/**
 * Haushalts-Essensplan (27.10.2026, Timo): Ein Essensplan gehört meist dem
 * **Haushalt** — jedes Mitglied isst ab Werk mit, mit eigener Portion, und
 * weicht ab, wo es will (der Sohn, der ins Studio geht, isst mehr Eiweiß).
 *
 * Drei Ebenen, drei Tabellen:
 *
 * - `household_meals` — der Plan des Haushalts (und Wünsche, `status =
 *   'wish'`). Das Gericht ist **eingefroren** (`FrozenDish`): Name, für wie
 *   viele Portionen die Zutaten gelten, Nährwerte je Portion, Zutaten mit
 *   Name und Regal. Kein Verweis auf ein Rezept oder Lebensmittel — die
 *   gehören einem Konto, der Plan dem Haushalt.
 * - `household_meal_eaters` — „wer isst mit, wie viel“: eine Zeile je
 *   Eintrag und Person, aber nur, wenn sie vom Werk abweicht (eine Portion,
 *   nichts dazu). Sie braucht der Haushalt für die Einkaufsliste.
 * - `meal_deviations` — die **persönlichen** Abweichungen mit allen
 *   Nährwerten (nicht mitessen, Faktor, Extra, eigene Variante) und die
 *   Dauerregeln (eine Zeile ohne Eintrag). Privat, mit dem Kontoschlüssel.
 *
 * Alles hier sind reine Funktionen — App und Server (Rechte der Kinder)
 * benutzen dieselben.
 */

import { stableId } from './ids.js';
import { cleanNutrients, scaleNutrients } from './nutrients.js';
import type { Nutrients } from './nutrients.js';
import { DAY_SLOTS, roundMacros, scaleMacros } from './nutrition.js';
import type { DaySlot, Macros } from './nutrition.js';
import { SHELF_CATEGORIES, itemKey } from './shopping.js';
import type { ShelfCategory } from './shopping.js';
import type { ChildLevel, MemberRole } from './modules.js';

// --- Grenzen -------------------------------------------------------------------

export const HOUSEHOLD_MEAL_STATUSES = ['planned', 'wish'] as const;
export type HouseholdMealStatus = (typeof HOUSEHOLD_MEAL_STATUSES)[number];

export const DISH_NAME_MAX = 120;
export const MEAL_NOTE_MAX = 500;
export const DISH_MAX_INGREDIENTS = 60;
export const MEAL_MAX_EXTRAS = 10;
/** Portionsfaktor je Person: ein Viertel bis vierfach. */
export const MEAL_FACTOR_MIN = 0.25;
export const MEAL_FACTOR_MAX = 4;
/** Portionen einer Person an einem Eintrag (Faktor × Variante) — mit Luft. */
export const EATER_PORTIONS_MAX = 10;
/** So weit voraus gleicht die App Protokoll und Mitess-Zeilen ab. */
export const HOUSEHOLD_MEAL_HORIZON_DAYS = 14;

// --- Formen ------------------------------------------------------------------

/** Eine Zutat des eingefrorenen Gerichts — für `portions` Portionen. */
export interface DishIngredient {
  name: string;
  /** `null` = freie Zeile ohne Menge („1 Prise Salz“) — steht auf der Liste als „prüfen“. */
  grams: number | null;
  category: ShelfCategory;
}

/**
 * Ein Gericht, wie es in den Haushalt geht: eingefroren. Ändert jemand
 * danach sein Rezept, bleibt der Plan, wie er war — wie beim Protokoll.
 */
export interface FrozenDish {
  name: string;
  /** Für wie viele Portionen die Zutaten gelten. */
  portions: number;
  /** Nährwerte einer Portion; `null` = unbekannt (dann zählt nichts in die Bilanz). */
  perPortion: Macros | null;
  /** Weitere Nährstoffe je Portion; fehlt ein Schlüssel, ist er unbekannt. */
  nutrients: Nutrients;
  /** Woher die Werte stammen — vom Rezept übernommen, nie erfunden. */
  sourceNote: string | null;
  ingredients: DishIngredient[];
  /** Zutaten als Text bei Gerichten ohne Zutatenliste — nicht gedeutet. */
  items: string | null;
}

/** Ein Gericht nur mit Namen (Wunsch, freier Eintrag): keine Werte, keine Zutaten. */
export function namedDish(name: string): FrozenDish {
  return {
    name: name.trim().slice(0, DISH_NAME_MAX),
    portions: 1,
    perPortion: null,
    nutrients: {},
    sourceNote: null,
    ingredients: [],
    items: null,
  };
}

/** Etwas dazu — aus einem eigenen Lebensmittel oder Gericht, Werte für genau diese Menge. */
export interface MealExtra {
  id: string;
  name: string;
  /** Menge in Gramm (Lebensmittel) — oder `null` bei Portionen eines Gerichts. */
  grams: number | null;
  portions: number | null;
  /** Werte für die ganze Menge, eingefroren beim Hinzufügen. */
  macros: Macros;
  nutrients: Nutrients;
  /** Die Quelle des Lebensmittels bzw. Gerichts — ohne Quelle keine Nährwerte. */
  sourceNote: string | null;
  category: ShelfCategory;
}

/** Dauerregeln einer Person (eine Zeile ohne Eintrag in `meal_deviations`). */
export interface MealRules {
  /** Portionsfaktor je Mahlzeit-Platz; fehlt einer, gilt 1. */
  factors: Partial<Record<DaySlot, number>>;
  /** An Trainingstagen nach dem Training (Post-Workout) dazu. */
  trainingExtra: MealExtra | null;
  /** „Statt Fleisch vegetarisch“ — ein Hinweis an den Haushalt, keine Rechnung. */
  vegetarian: boolean;
}

export const DEFAULT_MEAL_RULES: MealRules = {
  factors: {},
  trainingExtra: null,
  vegetarian: false,
};

/** Was eine Person an einem Eintrag anders macht — Inhalt einer `meal_deviations`-Zeile. */
export interface MealDeviation {
  /** „Ich esse nicht mit.“ */
  skip: boolean;
  /** Eigene Portion; `null` = Dauerregel bzw. 1. */
  factor: number | null;
  extras: MealExtra[];
  /** „Eigene Variante“ — ein anderes Gericht nur für mich. */
  variant: FrozenDish | null;
}

export const NO_DEVIATION: MealDeviation = { skip: false, factor: null, extras: [], variant: null };

/** Was der Haushalt von einer Person an einem Eintrag wissen muss (Einkauf, „wer isst mit“). */
export interface EaterShare {
  /** 0 = isst nicht mit. */
  portions: number;
  /** Was zusätzlich gekauft werden muss: Extras und die Zutaten der Variante. */
  items: DishIngredient[];
  /** Name der eigenen Variante — damit der Koch weiß, dass er nicht für alle kocht. */
  variant: string | null;
  vegetarian: boolean;
}

export const DEFAULT_SHARE: EaterShare = {
  portions: 1,
  items: [],
  variant: null,
  vegetarian: false,
};

// --- Feste Kennungen ---------------------------------------------------------

/**
 * Protokollzeile „Haushaltseintrag für mich“ — fest je Person und Eintrag,
 * wie `plannedLogId`. Legen zwei Geräte sie an, ist es dieselbe Zeile.
 */
export function householdLogId(userId: string, entryId: string): string {
  return stableId(`household-log|${userId}|${entryId}`);
}

/** Protokollzeile eines Extras an einem Haushaltseintrag. */
export function householdExtraLogId(userId: string, entryId: string, extraId: string): string {
  return stableId(`household-extra|${userId}|${entryId}|${extraId}`);
}

/** Protokollzeile des Trainings-Extras (Dauerregel) an einem Tag. */
export function trainingExtraLogId(userId: string, date: string): string {
  return stableId(`training-extra|${userId}|${date}`);
}

/** Mitess-Zeile — fest je Eintrag und Person, egal wer sie schreibt. */
export function eaterRowId(entryId: string, eaterId: string): string {
  return stableId(`household-eater|${entryId}|${eaterId}`);
}

/** Persönliche Abweichung an einem Eintrag. */
export function deviationId(userId: string, entryId: string): string {
  return stableId(`meal-deviation|${userId}|${entryId}`);
}

/** Die Dauerregeln — eine Zeile je Konto. */
export function mealRulesId(userId: string): string {
  return stableId(`meal-rules|${userId}`);
}

// --- Lesen (tolerant) --------------------------------------------------------

function finite(value: unknown, min: number, max: number): number | null {
  return typeof value === 'number' && Number.isFinite(value) && value >= min && value <= max
    ? value
    : null;
}

export function clampFactor(value: number): number {
  return Math.min(MEAL_FACTOR_MAX, Math.max(MEAL_FACTOR_MIN, Math.round(value * 100) / 100));
}

function isShelf(value: unknown): value is ShelfCategory {
  return typeof value === 'string' && (SHELF_CATEGORIES as readonly string[]).includes(value);
}

function isSlot(value: unknown): value is DaySlot {
  return typeof value === 'string' && (DAY_SLOTS as readonly string[]).includes(value);
}

function readMacros(raw: unknown): Macros | null {
  if (!raw || typeof raw !== 'object') return null;
  const entry = raw as Record<string, unknown>;
  const kcal = finite(entry.kcal, 0, 100_000);
  const protein = finite(entry.protein, 0, 10_000);
  const carbs = finite(entry.carbs, 0, 10_000);
  const fat = finite(entry.fat, 0, 10_000);
  if (kcal === null || protein === null || carbs === null || fat === null) return null;
  return { kcal, protein, carbs, fat };
}

export function readDish(raw: unknown): FrozenDish | null {
  if (!raw || typeof raw !== 'object') return null;
  const entry = raw as Record<string, unknown>;
  const name = typeof entry.name === 'string' ? entry.name.trim() : '';
  if (!name) return null;
  const ingredients = Array.isArray(entry.ingredients)
    ? (entry.ingredients as unknown[]).flatMap((item): DishIngredient[] => {
        if (!item || typeof item !== 'object') return [];
        const line = item as Record<string, unknown>;
        const label = typeof line.name === 'string' ? line.name.trim() : '';
        if (!label) return [];
        return [
          {
            name: label,
            grams: finite(line.grams, 0, 100_000),
            category: isShelf(line.category) ? line.category : 'other',
          },
        ];
      })
    : [];
  return {
    name,
    portions: finite(entry.portions, 0.1, 100) ?? 1,
    perPortion: readMacros(entry.perPortion),
    nutrients: cleanNutrients(entry.nutrients),
    sourceNote: typeof entry.sourceNote === 'string' ? entry.sourceNote : null,
    ingredients: ingredients.slice(0, DISH_MAX_INGREDIENTS),
    items: typeof entry.items === 'string' && entry.items.trim() ? entry.items : null,
  };
}

export function readExtra(raw: unknown): MealExtra | null {
  if (!raw || typeof raw !== 'object') return null;
  const entry = raw as Record<string, unknown>;
  const name = typeof entry.name === 'string' ? entry.name.trim() : '';
  const macros = readMacros(entry.macros);
  if (!name || !macros || typeof entry.id !== 'string') return null;
  return {
    id: entry.id,
    name,
    grams: finite(entry.grams, 0, 100_000),
    portions: finite(entry.portions, 0, 100),
    macros,
    nutrients: cleanNutrients(entry.nutrients),
    sourceNote: typeof entry.sourceNote === 'string' ? entry.sourceNote : null,
    category: isShelf(entry.category) ? entry.category : 'other',
  };
}

function readExtras(raw: unknown): MealExtra[] {
  if (!Array.isArray(raw)) return [];
  return (raw as unknown[])
    .map(readExtra)
    .filter((extra): extra is MealExtra => extra !== null)
    .slice(0, MEAL_MAX_EXTRAS);
}

/** Dauerregeln lesen — Unbekanntes fällt weg, ein Faktor außerhalb der Grenzen auch. */
export function mealRules(stored: unknown): MealRules {
  if (!stored || typeof stored !== 'object') return { ...DEFAULT_MEAL_RULES, factors: {} };
  const entry = stored as Record<string, unknown>;
  const factors: Partial<Record<DaySlot, number>> = {};
  if (entry.factors && typeof entry.factors === 'object') {
    for (const [slot, value] of Object.entries(entry.factors as Record<string, unknown>)) {
      const factor = finite(value, MEAL_FACTOR_MIN, MEAL_FACTOR_MAX);
      if (isSlot(slot) && factor !== null && factor !== 1) factors[slot] = factor;
    }
  }
  return {
    factors,
    trainingExtra: readExtra(entry.trainingExtra),
    vegetarian: entry.vegetarian === true,
  };
}

/** Abweichung lesen — fehlende Felder sind „wie der Haushalt“. */
export function readDeviation(row: {
  skip?: unknown;
  factor?: unknown;
  extras?: unknown;
  variant?: unknown;
}): MealDeviation {
  return {
    skip: row.skip === true,
    factor: finite(row.factor, MEAL_FACTOR_MIN, MEAL_FACTOR_MAX),
    extras: readExtras(row.extras),
    variant: readDish(row.variant),
  };
}

export function isNoDeviation(deviation: MealDeviation): boolean {
  return (
    !deviation.skip &&
    deviation.factor === null &&
    deviation.extras.length === 0 &&
    deviation.variant === null
  );
}

// --- Was ich esse ------------------------------------------------------------

export interface Serving {
  eats: boolean;
  /** Das Gericht des Haushalts — oder meine Variante. */
  dish: FrozenDish;
  variant: boolean;
  /** Portionen des Gerichts (bzw. der Variante) für mich. */
  portions: number;
  extras: MealExtra[];
  /** Woher der Faktor kommt — für die Anzeige. */
  factorFrom: 'default' | 'rule' | 'entry';
  vegetarian: boolean;
}

/**
 * Was ich an einem Haushaltseintrag esse.
 *
 * Reihenfolge: „nicht mit“ schlägt alles; die Portion am Eintrag schlägt die
 * Dauerregel für den Platz, die schlägt 1. Eine Variante ersetzt das Gericht,
 * die Portion gilt für sie genauso.
 */
export function servingFor(
  entry: { slot: DaySlot; dish: FrozenDish },
  deviation: MealDeviation | null,
  rules: MealRules,
): Serving {
  const own = deviation ?? NO_DEVIATION;
  const ruleFactor = rules.factors[entry.slot];
  const factorFrom = own.factor !== null ? 'entry' : ruleFactor !== undefined ? 'rule' : 'default';
  const portions = own.factor ?? ruleFactor ?? 1;
  return {
    eats: !own.skip,
    dish: own.variant ?? entry.dish,
    variant: own.variant !== null,
    portions,
    extras: own.skip ? [] : own.extras,
    factorFrom,
    vegetarian: rules.vegetarian,
  };
}

/**
 * Werte für das Protokoll — `null`, wenn das Gericht keine Nährwerte hat.
 * Eine Zeile mit 0 kcal sähe aus wie eine Angabe.
 */
export function dishLogValues(
  dish: FrozenDish,
  portions: number,
): { macros: Macros; nutrients: Nutrients } | null {
  if (!dish.perPortion || !(portions > 0)) return null;
  return {
    macros: roundMacros(scaleMacros(dish.perPortion, portions)),
    nutrients: scaleNutrients(dish.nutrients, portions),
  };
}

/** Zutaten eines Gerichts für `portions` Portionen (Menge unbekannt bleibt unbekannt). */
export function scaledIngredients(dish: FrozenDish, portions: number): DishIngredient[] {
  const factor = dish.portions > 0 ? portions / dish.portions : 0;
  return dish.ingredients.map((line) => ({
    ...line,
    grams: line.grams === null ? null : Math.round(line.grams * factor * 10) / 10,
  }));
}

/**
 * Was der Haushalt von meinem Teller wissen muss: die Portion vom Gericht
 * des Haushalts (0 bei Variante oder „nicht mit“) und was zusätzlich zu
 * kaufen ist (Zutaten der Variante, Extras mit Menge). Nährwerte gehen den
 * Haushalt nichts an — sie bleiben in der persönlichen Zeile.
 */
export function eaterShare(serving: Serving): EaterShare {
  if (!serving.eats) {
    return { portions: 0, items: [], variant: null, vegetarian: serving.vegetarian };
  }
  const items: DishIngredient[] = serving.variant
    ? scaledIngredients(serving.dish, serving.portions)
    : [];
  for (const extra of serving.extras) {
    items.push({ name: extra.name, grams: extra.grams, category: extra.category });
  }
  return {
    portions: serving.variant ? 0 : Math.round(serving.portions * 100) / 100,
    items,
    variant: serving.variant ? serving.dish.name : null,
    vegetarian: serving.vegetarian,
  };
}

export function isDefaultShare(share: EaterShare): boolean {
  return (
    share.portions === 1 && share.items.length === 0 && share.variant === null && !share.vegetarian
  );
}

export function sameShare(a: EaterShare, b: EaterShare): boolean {
  return JSON.stringify(normalShare(a)) === JSON.stringify(normalShare(b));
}

function normalShare(share: EaterShare): EaterShare {
  return {
    portions: share.portions,
    items: share.items.map((item) => ({
      name: item.name,
      grams: item.grams,
      category: item.category,
    })),
    variant: share.variant,
    vegetarian: share.vegetarian,
  };
}

/** Mitess-Zeile lesen — eine fehlende heißt: isst mit, eine Portion. */
export function readShare(row: {
  portions?: unknown;
  items?: unknown;
  variant?: unknown;
  vegetarian?: unknown;
}): EaterShare {
  const items = Array.isArray(row.items)
    ? (row.items as unknown[]).flatMap((raw): DishIngredient[] => {
        if (!raw || typeof raw !== 'object') return [];
        const line = raw as Record<string, unknown>;
        const name = typeof line.name === 'string' ? line.name.trim() : '';
        if (!name) return [];
        return [
          {
            name,
            grams: finite(line.grams, 0, 100_000),
            category: isShelf(line.category) ? line.category : 'other',
          },
        ];
      })
    : [];
  return {
    portions: finite(row.portions, 0, EATER_PORTIONS_MAX) ?? 1,
    items,
    variant: typeof row.variant === 'string' && row.variant ? row.variant : null,
    vegetarian: row.vegetarian === true,
  };
}

// --- Wer isst mit ------------------------------------------------------------

export interface EaterRow {
  entryId: string;
  eaterId: string;
  deletedAt?: string | null;
  portions?: unknown;
  items?: unknown;
  variant?: unknown;
  vegetarian?: unknown;
}

export interface EaterLine {
  userId: string;
  share: EaterShare;
  /** Ohne eigene Zeile — der Stand ab Werk. */
  byDefault: boolean;
}

/**
 * Wer an einem Eintrag mitisst: **jedes aktive Mitglied**, ab Werk mit
 * einer Portion. Zeilen von Menschen, die nicht (mehr) im Haushalt sind,
 * zählen nicht — wer auszog, isst nicht mehr mit.
 */
export function eatersOf(
  entryId: string,
  memberIds: readonly string[],
  rows: readonly EaterRow[],
): EaterLine[] {
  const byEater = new Map(
    rows
      .filter((row) => row.entryId === entryId && !row.deletedAt)
      .map((row) => [row.eaterId, row] as const),
  );
  return memberIds.map((userId) => {
    const row = byEater.get(userId);
    return row
      ? { userId, share: readShare(row), byDefault: false }
      : { userId, share: { ...DEFAULT_SHARE, items: [] }, byDefault: true };
  });
}

/** Portionen vom Gericht des Haushalts, über alle Mitesser. */
export function totalPortions(lines: readonly EaterLine[]): number {
  return Math.round(lines.reduce((sum, line) => sum + line.share.portions, 0) * 100) / 100;
}

// --- Einkauf -----------------------------------------------------------------

export interface HouseholdNeedsEntry {
  id: string;
  date: string;
  dish: FrozenDish;
}

/**
 * Bedarf aus dem Haushaltsplan, in der Form der Einkaufsrechnung: je
 * Zutat die Menge für alle Portionen der Mitesser, dazu, was jemand extra
 * braucht. Ein Gericht ohne Zutatenliste steht als „ohne Zutaten“ da.
 */
export interface HouseholdNeedLine {
  name: string;
  category: ShelfCategory;
  grams: number | null;
  usedFor: string;
}

export interface HouseholdNeeds {
  lines: HouseholdNeedLine[];
  unstructured: { id: string; name: string; items: string | null; date: string }[];
}

export function householdNeeds(
  entries: readonly HouseholdNeedsEntry[],
  memberIds: readonly string[],
  eaters: readonly EaterRow[],
): HouseholdNeeds {
  const lines: HouseholdNeedLine[] = [];
  const unstructured: HouseholdNeeds['unstructured'] = [];
  for (const entry of entries) {
    const who = eatersOf(entry.id, memberIds, eaters);
    const portions = totalPortions(who);
    if (portions > 0) {
      if (entry.dish.ingredients.length === 0) {
        unstructured.push({
          id: entry.id,
          name: entry.dish.name,
          items: entry.dish.items,
          date: entry.date,
        });
      } else {
        for (const line of scaledIngredients(entry.dish, portions)) {
          lines.push({ ...line, usedFor: entry.dish.name });
        }
      }
    }
    for (const eater of who) {
      for (const item of eater.share.items) {
        lines.push({ ...item, usedFor: eater.share.variant ?? entry.dish.name });
      }
    }
  }
  return { lines, unstructured };
}

// --- Bilanz ------------------------------------------------------------------

/**
 * „Dir fehlen X g zu deinem eigenen Eiweißziel“ — eine Feststellung gegen
 * das **eigene** Ziel, nie eine Vorschrift. `null` ohne Ziel oder wenn
 * nichts fehlt.
 */
export function proteinGap(eatenG: number, targetG: number | null | undefined): number | null {
  if (!targetG || targetG <= 0) return null;
  const gap = Math.round(targetG - eatenG);
  return gap > 0 ? gap : null;
}

/** Ein Protokolleintrag „von Hand“ für denselben Platz und dasselbe Gericht? Dann nicht doppelt. */
export function isSameMeal(
  row: { slot: string; label: string; deletedAt?: string | null },
  slot: DaySlot,
  name: string,
): boolean {
  return !row.deletedAt && row.slot === slot && itemKey(row.label) === itemKey(name);
}

// --- Rechte ------------------------------------------------------------------

export interface MealActor {
  role: MemberRole;
  /** Nur bei Kindern; leer = `school`. */
  level: ChildLevel | null;
}

/**
 * Darf jemand einen Eintrag des Haushaltsplans schreiben? `null` = ja,
 * sonst der Grund. Dieselbe Regel für Server (Push) und App (Knöpfe).
 *
 * - Erwachsene schreiben alles, auch Wünsche übernehmen oder ablehnen.
 * - Kinder schreiben nie den Plan. Ab `school` tragen sie **Wünsche** ein und
 *   ändern oder löschen nur ihre eigenen, solange es Wünsche sind.
 * - Kleine Kinder lesen nur.
 */
export function householdMealWriteRule(
  actor: MealActor,
  userId: string,
  incoming: { status: string },
  existing: { status: string; userId: string } | null,
): 'small' | 'planOnlyAdults' | 'ownWishes' | null {
  if (actor.role === 'adult') return null;
  if ((actor.level ?? 'school') === 'small') return 'small';
  if (incoming.status !== 'wish') return 'planOnlyAdults';
  if (existing && (existing.status !== 'wish' || existing.userId !== userId)) return 'ownWishes';
  return null;
}

/**
 * Wer die Mitess-Zeile einer Person schreiben darf: die Person selbst (ab
 * `school`), und Erwachsene für die **Kinder** des Haushalts. Die Portion
 * eines anderen Erwachsenen ist dessen Sache.
 */
export function eaterWriteRule(
  actor: MealActor,
  userId: string,
  eater: { userId: string; role: MemberRole } | null,
): 'small' | 'notMember' | 'othersPortion' | null {
  if (!eater) return 'notMember';
  if (actor.role === 'child' && (actor.level ?? 'school') === 'small') return 'small';
  if (eater.userId === userId) return null;
  if (actor.role === 'adult' && eater.role === 'child') return null;
  return 'othersPortion';
}
